import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Motorcycle, Storefront, User as UserIcon } from "@phosphor-icons/react";
import { TID } from "@/lib/testIds";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", { name, phone, address });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const switchRole = async (role) => {
    try {
      await api.post("/profile/role", { role });
      await refresh();
      toast.success(`Switched to ${role.replace("_", " ")}`);
      if (role === "driver") navigate("/driver");
      if (role === "store_admin") navigate("/admin");
      if (role === "customer") navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch role");
    }
  };

  const roles = [
    { key: "customer", tid: TID.rolePickerCustomer, icon: UserIcon, label: "Shopper", d: "Browse & order", colour: "var(--red)" },
    { key: "driver", tid: TID.rolePickerDriver, icon: Motorcycle, label: "Driver", d: "Deliver orders", colour: "var(--blue)" },
    { key: "store_admin", tid: TID.rolePickerStore, icon: Storefront, label: "Shop staff", d: "Manage stock & orders", colour: "var(--green)" },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <span className="label-eyebrow">Your card</span>
        <h1 className="mt-1 font-display text-5xl">Profile</h1>
        <p className="mt-2 text-sm text-[var(--text-2)]">{user?.email}</p>
      </div>

      <div className="card-panel p-6 space-y-4">
        <div>
          <label className="label-eyebrow">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            className="mt-2 bg-white border-2 border-[var(--text)] h-11 rounded-full"
            style={{ boxShadow: "3px 3px 0 0 var(--text)" }} />
        </div>
        <div>
          <label className="label-eyebrow">Phone</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)}
            className="mt-2 bg-white border-2 border-[var(--text)] h-11 rounded-full" placeholder="+27 71 234 5678"
            style={{ boxShadow: "3px 3px 0 0 var(--text)" }} />
        </div>
        <div>
          <label className="label-eyebrow">Default address</label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)}
            className="mt-2 bg-white border-2 border-[var(--text)] h-11 rounded-full" placeholder="72 Kloof Street, Cape Town"
            style={{ boxShadow: "3px 3px 0 0 var(--text)" }} />
        </div>
        <button onClick={save} disabled={saving} className="btn-primary">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div>
        <span className="label-eyebrow">Demo · Try another role</span>
        <p className="mt-2 text-sm text-[var(--text-2)]">In production this would be an approved onboarding flow. For the MVP, jump between roles to explore the driver & shop dashboards.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {roles.map((r) => (
            <button
              key={r.key}
              data-testid={r.tid}
              onClick={() => switchRole(r.key)}
              className="card-panel p-5 text-left"
            >
              <div
                className="w-12 h-12 rounded-full border-2 border-[var(--text)] flex items-center justify-center"
                style={{ background: r.colour, boxShadow: "3px 3px 0 0 var(--text)" }}
              >
                <r.icon size={22} weight="fill" className="text-white" />
              </div>
              <p className="mt-4 font-display text-xl">{r.label}</p>
              <p className="text-xs font-bold text-[var(--text-2)] uppercase tracking-widest mt-1">{r.d}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
